/**
 * ProceduralAudio.ts
 * Generates retro-style music and sound effects procedurally using Web Audio API
 * No audio files needed - all sounds are synthesized in real-time
 */

import {
  AudioTrack,
  SoundEffect,
  MUSIC_DEFINITIONS,
  SOUND_DEFINITIONS,
} from './data/AudioData';

/**
 * Musical note frequencies (A4 = 440Hz standard)
 */
const NOTES = {
  C: [16.35, 32.7, 65.41, 130.81, 261.63, 523.25, 1046.5, 2093.0, 4186.01],
  Cs: [17.32, 34.65, 69.3, 138.59, 277.18, 554.37, 1108.73, 2217.46, 4434.92],
  D: [18.35, 36.71, 73.42, 146.83, 293.66, 587.33, 1174.66, 2349.32, 4698.63],
  Ds: [19.45, 38.89, 77.78, 155.56, 311.13, 622.25, 1244.51, 2489.02, 4978.03],
  E: [20.6, 41.2, 82.41, 164.81, 329.63, 659.25, 1318.51, 2637.02, 5274.04],
  F: [21.83, 43.65, 87.31, 174.61, 349.23, 698.46, 1396.91, 2793.83, 5587.65],
  Fs: [23.12, 46.25, 92.5, 185.0, 369.99, 739.99, 1479.98, 2959.96, 5919.91],
  G: [24.5, 49.0, 98.0, 196.0, 392.0, 783.99, 1567.98, 3135.96, 6271.93],
  Gs: [25.96, 51.91, 103.83, 207.65, 415.3, 830.61, 1661.22, 3322.44, 6644.88],
  A: [27.5, 55.0, 110.0, 220.0, 440.0, 880.0, 1760.0, 3520.0, 7040.0],
  As: [29.14, 58.27, 116.54, 233.08, 466.16, 932.33, 1864.66, 3729.31, 7458.62],
  B: [30.87, 61.74, 123.47, 246.94, 493.88, 987.77, 1975.53, 3951.07, 7902.13],
};

/**
 * Helper to get note frequency
 * @param note Note name (C, Cs, D, etc.)
 * @param octave Octave (0-8)
 */
function note(n: keyof typeof NOTES, octave: number): number {
  return NOTES[n][octave] || 440; // Fallback to A4
}

/**
 * Simple chord progressions for different moods
 */
const CHORD_PROGRESSIONS = {
  epic: [
    // I - V - vi - IV (Epic progression)
    [note('C', 4), note('E', 4), note('G', 4)], // C major
    [note('G', 3), note('B', 3), note('D', 4)], // G major
    [note('A', 3), note('C', 4), note('E', 4)], // A minor
    [note('F', 3), note('A', 3), note('C', 4)], // F major
  ],
  peaceful: [
    // I - IV - V - I (Simple, calming)
    [note('C', 4), note('E', 4), note('G', 4)], // C major
    [note('F', 3), note('A', 3), note('C', 4)], // F major
    [note('G', 3), note('B', 3), note('D', 4)], // G major
    [note('C', 4), note('E', 4), note('G', 4)], // C major
  ],
  tense: [
    // Minor progression with tension
    [note('A', 3), note('C', 4), note('E', 4)], // A minor
    [note('F', 3), note('A', 3), note('C', 4)], // F major
    [note('G', 3), note('B', 3), note('D', 4)], // G major
    [note('E', 3), note('G', 3), note('B', 3)], // E minor
  ],
  exciting: [
    // Fast-paced progression
    [note('D', 4), note('Fs', 4), note('A', 4)], // D major
    [note('A', 3), note('Cs', 4), note('E', 4)], // A major
    [note('B', 3), note('Ds', 4), note('Fs', 4)], // B major
    [note('Fs', 3), note('As', 3), note('Cs', 4)], // F# major
  ],
  victorious: [
    // Triumphant progression
    [note('C', 4), note('E', 4), note('G', 4)], // C major
    [note('F', 4), note('A', 4), note('C', 5)], // F major (high)
    [note('G', 4), note('B', 4), note('D', 5)], // G major (high)
    [note('C', 5), note('E', 5), note('G', 5)], // C major (high)
  ],
  somber: [
    // Sad, slow progression
    [note('A', 3), note('C', 4), note('E', 4)], // A minor
    [note('F', 3), note('A', 3), note('C', 4)], // F major
    [note('C', 3), note('E', 3), note('G', 3)], // C major (low)
    [note('A', 2), note('C', 3), note('E', 3)], // A minor (low)
  ],
};

/**
 * Simple melody patterns for different moods
 */
const MELODY_PATTERNS = {
  epic: [0, 4, 7, 9, 7, 4, 2, 0],
  peaceful: [0, 2, 4, 5, 4, 2, 0, -1],
  tense: [0, 1, 3, 1, 0, -1, 0, 1],
  exciting: [0, 5, 7, 9, 12, 9, 7, 5],
  victorious: [0, 4, 7, 12, 16, 12, 7, 4],
  somber: [0, -2, -3, -5, -3, -2, 0, -1],
};

export class ProceduralAudio {
  private audioContext: AudioContext;
  private sampleRate: number;

  // Cache for generated music
  private musicCache: Map<AudioTrack, AudioBuffer> = new Map();

  constructor(audioContext: AudioContext) {
    this.audioContext = audioContext;
    this.sampleRate = audioContext.sampleRate;
  }

  // =========================================================================
  // Music Generation
  // =========================================================================

  /**
   * Generate a music track
   */
  async generateMusic(track: AudioTrack): Promise<AudioBuffer | null> {
    // Check cache first
    if (this.musicCache.has(track)) {
      return this.musicCache.get(track)!;
    }

    const definition = MUSIC_DEFINITIONS[track];
    if (!definition) {
      console.warn(`No definition for music track: ${track}`);
      return null;
    }

    // Generate based on mood
    let buffer: AudioBuffer;
    const duration = definition.duration || 16000; // Default 16 seconds for loops

    switch (definition.mood) {
      case 'epic':
        buffer = this.generateEpicTheme(duration, definition.tempo);
        break;
      case 'peaceful':
        buffer = this.generatePeacefulTheme(duration, definition.tempo);
        break;
      case 'tense':
        buffer = this.generateTenseTheme(duration, definition.tempo);
        break;
      case 'exciting':
        buffer = this.generateExcitingTheme(duration, definition.tempo);
        break;
      case 'victorious':
        buffer = this.generateVictoriousTheme(duration, definition.tempo);
        break;
      case 'somber':
        buffer = this.generateSomberTheme(duration, definition.tempo);
        break;
      default:
        buffer = this.generateSimpleTheme(duration, definition.tempo);
    }

    // Cache the generated music
    this.musicCache.set(track, buffer);

    return buffer;
  }

  private generateEpicTheme(duration: number, tempo: number): AudioBuffer {
    const sampleRate = this.sampleRate;
    const numSamples = Math.floor((duration / 1000) * sampleRate);
    const buffer = this.audioContext.createBuffer(2, numSamples, sampleRate);

    const leftChannel = buffer.getChannelData(0);
    const rightChannel = buffer.getChannelData(1);

    const progression = CHORD_PROGRESSIONS.epic;
    const melody = MELODY_PATTERNS.epic;
    const beatDuration = 60 / tempo; // seconds per beat
    const measureDuration = beatDuration * 4; // 4/4 time

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const measure = Math.floor(t / measureDuration);
      const chordIndex = measure % progression.length;
      const chord = progression[chordIndex] || [note('C', 4), note('E', 4), note('G', 4)];

      // Bass line (root note, one octave lower)
      const bassFreq = chord[0]! / 2;
      const bass = Math.sin(2 * Math.PI * bassFreq * t) * 0.15;

      // Chord (all notes)
      let chordSound = 0;
      for (const freq of chord) {
        chordSound += Math.sin(2 * Math.PI * freq * t) * 0.08;
      }

      // Melody line
      const melodyBeat = Math.floor((t % measureDuration) / beatDuration);
      const melodyOffset = melody[melodyBeat % melody.length] || 0;
      const melodyFreq = chord[0]! * Math.pow(2, melodyOffset / 12);
      const melodySound = Math.sin(2 * Math.PI * melodyFreq * t) * 0.12;

      // Simple envelope
      const beatPhase = (t % beatDuration) / beatDuration;
      const envelope = Math.max(0, 1 - beatPhase * 0.5);

      const sample = (bass + chordSound + melodySound * envelope) * 0.5;
      leftChannel[i] = sample;
      rightChannel[i] = sample;
    }

    return buffer;
  }

  private generatePeacefulTheme(duration: number, tempo: number): AudioBuffer {
    const sampleRate = this.sampleRate;
    const numSamples = Math.floor((duration / 1000) * sampleRate);
    const buffer = this.audioContext.createBuffer(2, numSamples, sampleRate);

    const leftChannel = buffer.getChannelData(0);
    const rightChannel = buffer.getChannelData(1);

    const progression = CHORD_PROGRESSIONS.peaceful;
    const melody = MELODY_PATTERNS.peaceful;
    const beatDuration = 60 / tempo;
    const measureDuration = beatDuration * 4;

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const measure = Math.floor(t / measureDuration);
      const chordIndex = measure % progression.length;
      const chord = progression[chordIndex] || [note('C', 4), note('E', 4), note('G', 4)];

      // Soft arpeggiated chords
      let arp = 0;
      const arpSpeed = 8; // notes per measure
      const arpIndex = Math.floor((t % measureDuration) / (measureDuration / arpSpeed));
      const arpFreq = chord[arpIndex % chord.length] || chord[0]!;
      arp = Math.sin(2 * Math.PI * arpFreq * t) * 0.15;

      // Gentle melody
      const melodyBeat = Math.floor((t % measureDuration) / beatDuration);
      const melodyOffset = melody[melodyBeat % melody.length] || 0;
      const melodyFreq = chord[0]! * Math.pow(2, melodyOffset / 12);
      const melodySound = Math.sin(2 * Math.PI * melodyFreq * t) * 0.1;

      const sample = (arp + melodySound) * 0.4;
      leftChannel[i] = sample;
      rightChannel[i] = sample;
    }

    return buffer;
  }

  private generateTenseTheme(duration: number, tempo: number): AudioBuffer {
    const sampleRate = this.sampleRate;
    const numSamples = Math.floor((duration / 1000) * sampleRate);
    const buffer = this.audioContext.createBuffer(2, numSamples, sampleRate);

    const leftChannel = buffer.getChannelData(0);
    const rightChannel = buffer.getChannelData(1);

    const progression = CHORD_PROGRESSIONS.tense;
    const beatDuration = 60 / tempo;
    const measureDuration = beatDuration * 4;

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const measure = Math.floor(t / measureDuration);
      const chordIndex = measure % progression.length;
      const chord = progression[chordIndex] || [note('A', 3), note('C', 4), note('E', 4)];

      // Pulsing bass with slight dissonance
      const bassFreq = chord[0]! / 2;
      const pulse = Math.floor((t % beatDuration) / (beatDuration / 8)) % 2;
      const bass = Math.sin(2 * Math.PI * bassFreq * t) * 0.2 * pulse;

      // Dissonant chord
      let chordSound = 0;
      for (let j = 0; j < chord.length; j++) {
        const freq = chord[j]! * (1 + Math.sin(t * 2) * 0.01); // slight detuning
        chordSound += Math.sin(2 * Math.PI * freq * t) * 0.08;
      }

      const sample = (bass + chordSound) * 0.5;
      leftChannel[i] = sample;
      rightChannel[i] = sample;
    }

    return buffer;
  }

  private generateExcitingTheme(duration: number, tempo: number): AudioBuffer {
    const sampleRate = this.sampleRate;
    const numSamples = Math.floor((duration / 1000) * sampleRate);
    const buffer = this.audioContext.createBuffer(2, numSamples, sampleRate);

    const leftChannel = buffer.getChannelData(0);
    const rightChannel = buffer.getChannelData(1);

    const progression = CHORD_PROGRESSIONS.exciting;
    const melody = MELODY_PATTERNS.exciting;
    const beatDuration = 60 / tempo;
    const measureDuration = beatDuration * 4;

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const measure = Math.floor(t / measureDuration);
      const chordIndex = measure % progression.length;
      const chord = progression[chordIndex] || [note('C', 4), note('E', 4), note('G', 4)];

      // Driving bass
      const bassFreq = chord[0]! / 2;
      const bass = Math.sin(2 * Math.PI * bassFreq * t) * 0.2;

      // Fast arpeggios
      const arpSpeed = 16;
      const arpIndex = Math.floor((t % measureDuration) / (measureDuration / arpSpeed));
      const arpFreq = (chord[arpIndex % chord.length] || chord[0]!) * 2;
      const arp = Math.sin(2 * Math.PI * arpFreq * t) * 0.1;

      // Energetic melody
      const melodyBeat = Math.floor((t % measureDuration) / beatDuration);
      const melodyOffset = melody[melodyBeat % melody.length] || 0;
      const melodyFreq = chord[0]! * Math.pow(2, melodyOffset / 12);
      const melodySound = Math.sin(2 * Math.PI * melodyFreq * t) * 0.15;

      const sample = (bass + arp + melodySound) * 0.5;
      leftChannel[i] = sample;
      rightChannel[i] = sample;
    }

    return buffer;
  }

  private generateVictoriousTheme(duration: number, tempo: number): AudioBuffer {
    const sampleRate = this.sampleRate;
    const numSamples = Math.floor((duration / 1000) * sampleRate);
    const buffer = this.audioContext.createBuffer(2, numSamples, sampleRate);

    const leftChannel = buffer.getChannelData(0);
    const rightChannel = buffer.getChannelData(1);

    const progression = CHORD_PROGRESSIONS.victorious;
    const melody = MELODY_PATTERNS.victorious;
    const beatDuration = 60 / tempo;
    const measureDuration = beatDuration * 4;

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const measure = Math.floor(t / measureDuration);
      const chordIndex = measure % progression.length;
      const chord = progression[chordIndex] || [note('C', 4), note('E', 4), note('G', 4)];

      // Strong bass
      const bassFreq = chord[0]! / 2;
      const bass = Math.sin(2 * Math.PI * bassFreq * t) * 0.2;

      // Bright chords
      let chordSound = 0;
      for (const freq of chord) {
        chordSound += Math.sin(2 * Math.PI * freq * t) * 0.1;
      }

      // Triumphant melody
      const melodyBeat = Math.floor((t % measureDuration) / beatDuration);
      const melodyOffset = melody[melodyBeat % melody.length] || 0;
      const melodyFreq = chord[0]! * Math.pow(2, melodyOffset / 12);
      const melodySound = Math.sin(2 * Math.PI * melodyFreq * t) * 0.15;

      // Crescendo over time
      const crescendo = Math.min(1, t / (duration / 1000 / 2));

      const sample = (bass + chordSound + melodySound) * 0.5 * crescendo;
      leftChannel[i] = sample;
      rightChannel[i] = sample;
    }

    return buffer;
  }

  private generateSomberTheme(duration: number, tempo: number): AudioBuffer {
    const sampleRate = this.sampleRate;
    const numSamples = Math.floor((duration / 1000) * sampleRate);
    const buffer = this.audioContext.createBuffer(2, numSamples, sampleRate);

    const leftChannel = buffer.getChannelData(0);
    const rightChannel = buffer.getChannelData(1);

    const progression = CHORD_PROGRESSIONS.somber;
    const melody = MELODY_PATTERNS.somber;
    const beatDuration = 60 / tempo;
    const measureDuration = beatDuration * 4;

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const measure = Math.floor(t / measureDuration);
      const chordIndex = measure % progression.length;
      const chord = progression[chordIndex] || [note('C', 4), note('E', 4), note('G', 4)];

      // Low, sustained bass
      const bassFreq = chord[0]! / 2;
      const bass = Math.sin(2 * Math.PI * bassFreq * t) * 0.15;

      // Slow, sad melody
      const melodyBeat = Math.floor((t % (measureDuration * 2)) / (beatDuration * 2));
      const melodyOffset = melody[melodyBeat % melody.length] || 0;
      const melodyFreq = chord[0]! * Math.pow(2, melodyOffset / 12);
      const melodySound = Math.sin(2 * Math.PI * melodyFreq * t) * 0.12;

      // Fade out at end
      const fadeOut = t > (duration / 1000) * 0.7 ? 1 - ((t - (duration / 1000) * 0.7) / ((duration / 1000) * 0.3)) : 1;

      const sample = (bass + melodySound) * 0.4 * fadeOut;
      leftChannel[i] = sample;
      rightChannel[i] = sample;
    }

    return buffer;
  }

  private generateSimpleTheme(duration: number, _tempo: number): AudioBuffer {
    // Fallback simple theme
    const sampleRate = this.sampleRate;
    const numSamples = Math.floor((duration / 1000) * sampleRate);
    const buffer = this.audioContext.createBuffer(2, numSamples, sampleRate);

    const leftChannel = buffer.getChannelData(0);
    const rightChannel = buffer.getChannelData(1);

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const freq = 440; // A4
      const sample = Math.sin(2 * Math.PI * freq * t) * 0.1;
      leftChannel[i] = sample;
      rightChannel[i] = sample;
    }

    return buffer;
  }

  // =========================================================================
  // Sound Effect Generation
  // =========================================================================

  /**
   * Generate a sound effect
   */
  async generateSFX(effect: SoundEffect): Promise<AudioBuffer | null> {
    const definition = SOUND_DEFINITIONS[effect];
    if (!definition) {
      console.warn(`No definition for sound effect: ${effect}`);
      return null;
    }

    return this.generateSound(definition);
  }

  private generateSound(def: any): AudioBuffer {
    const sampleRate = this.sampleRate;
    const numSamples = Math.floor((def.duration / 1000) * sampleRate);
    const buffer = this.audioContext.createBuffer(1, numSamples, sampleRate);
    const channel = buffer.getChannelData(0);

    const isFreqSweep = Array.isArray(def.frequency);
    const startFreq = isFreqSweep ? def.frequency[0] : def.frequency;
    const endFreq = isFreqSweep ? def.frequency[def.frequency.length - 1] : def.frequency;

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const progress = i / numSamples;

      // Frequency sweep
      let freq = startFreq;
      if (isFreqSweep) {
        // For multi-note sequences
        if (def.frequency.length > 2) {
          const noteIndex = Math.floor(progress * def.frequency.length);
          freq = def.frequency[Math.min(noteIndex, def.frequency.length - 1)];
        } else {
          freq = startFreq + (endFreq - startFreq) * progress;
        }
      }

      // Generate waveform
      let sample = 0;
      switch (def.type) {
        case 'sine':
          sample = Math.sin(2 * Math.PI * freq * t);
          break;
        case 'square':
          sample = Math.sin(2 * Math.PI * freq * t) > 0 ? 1 : -1;
          break;
        case 'sawtooth':
          sample = 2 * ((freq * t) % 1) - 1;
          break;
        case 'triangle':
          sample = 2 * Math.abs(2 * ((freq * t) % 1) - 1) - 1;
          break;
        case 'noise':
          sample = Math.random() * 2 - 1;
          break;
      }

      // Apply envelope if defined
      let envelope = 1;
      if (def.envelope) {
        const attackTime = def.envelope.attack / 1000;
        const decayTime = def.envelope.decay / 1000;
        const releaseTime = def.envelope.release / 1000;
        const totalTime = def.duration / 1000;

        if (t < attackTime) {
          // Attack phase
          envelope = t / attackTime;
        } else if (t < attackTime + decayTime) {
          // Decay phase
          const decayProgress = (t - attackTime) / decayTime;
          envelope = 1 - (1 - def.envelope.sustain) * decayProgress;
        } else if (t < totalTime - releaseTime) {
          // Sustain phase
          envelope = def.envelope.sustain;
        } else {
          // Release phase
          const releaseProgress = (t - (totalTime - releaseTime)) / releaseTime;
          envelope = def.envelope.sustain * (1 - releaseProgress);
        }
      } else {
        // Simple fade out
        envelope = 1 - progress * 0.5;
      }

      channel[i] = sample * envelope * def.volume;
    }

    return buffer;
  }

  /**
   * Clear music cache
   */
  clearCache(): void {
    this.musicCache.clear();
  }
}
